<script>
  import { fade, scale, fly } from 'svelte/transition';
  import { quintOut, backOut } from 'svelte/easing';
  import { loginAdmin } from '../os/session.svelte.js';
  import UserTile from './login/UserTile.svelte';

  let { onlogin } = $props();

  let adminMode = $state(false);
  let password = $state('');
  let code = $state('');
  let needCode = $state(false);
  let error = $state('');
  let loading = $state(false);

  function guest() {
    onlogin('guest');
  }

  function toggleAdmin() {
    adminMode = true;
    error = '';
  }

  // autofocus only fires on initial page load, not for fields revealed later.
  function focusOnMount(node) {
    node.focus();
  }

  function back() {
    adminMode = false;
    password = '';
    code = '';
    needCode = false;
    error = '';
  }

  async function submit() {
    error = '';
    loading = true;
    const res = await loginAdmin(password, needCode ? code : undefined);
    loading = false;
    if (res.ok) {
      onlogin('admin');
      return;
    }
    if (res.twoFactorRequired && !needCode) {
      needCode = true;
      code = '';
    }
    error = res.error;
  }
</script>

<div class="login-screen">
  <div class="login-wallpaper" aria-hidden="true"></div>
  <div class="login-overlay" aria-hidden="true"></div>
  <div class="login-fx" aria-hidden="true"></div>

  <div class="login-center" transition:scale={{ start: 0.9, easing: backOut, duration: 620 }}>
    <div class="login-panel" transition:fade={{ duration: 320 }}>
      <div class="panel-face">
        <div class="login-header">
          <div class="logo-stage" in:fly={{ y: -14, delay: 140, duration: 380, easing: quintOut }}>
            <svg class="login-logo" viewBox="0 0 384 512" aria-hidden="true">
              <path
                fill="currentColor"
                d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C72.7 141.9 32 175.6 32 244.1c0 20.2 3.7 41.1 11.1 62.6 9.9 28.3 45.6 97.7 82.8 96.6 19.4-.5 33.1-13.8 58.4-13.8 24.5 0 37.2 13.8 58.5 13.8 37.6-.5 69.9-63.7 79.4-92.1-50.4-23.8-47.9-69.7-47.9-71.5zM262.7 113.3c20.4-24.7 18.5-47.2 17.9-55.3-17.9 1-38.6 12.2-50.4 26-13 14.9-20.6 33.3-19 52.9 19.4 1.5 37.2-8.5 51.5-23.6z"
              />
            </svg>
          </div>
          <div class="title-stage" in:fly={{ y: -10, delay: 240, duration: 360, easing: quintOut }}>
            <h1 class="extruded">SMac OS X</h1>
          </div>
          <div in:fade={{ delay: 340, duration: 300 }}>
            <p class="login-version">10.6.8</p>
          </div>
        </div>

        {#if !adminMode}
          <div class="login-users">
            <div class="user-slot" in:fly={{ y: 18, delay: 300, duration: 420, easing: quintOut }}>
              <UserTile
                kind="sakura"
                name="Guest"
                desc="Click to enter"
                label="Enter as guest"
                onclick={guest}
              />
            </div>
            <div class="user-slot" in:fly={{ y: 18, delay: 400, duration: 420, easing: quintOut }}>
              <UserTile
                kind="ocean"
                name="Administrator"
                desc="Password required"
                label="Log in as administrator"
                onclick={toggleAdmin}
              />
            </div>
          </div>
        {:else}
          <div class="login-pw-form" transition:fly={{ y: 16, duration: 300, easing: quintOut }}>
            <button class="login-back" onclick={back}>← Back</button>
            <label class="login-pw-label" for="admin-pw">Enter password:</label>
            <div class="pw-well">
              <input
                id="admin-pw"
                class="login-pw-input"
                type="password"
                bind:value={password}
                placeholder="Password"
                readonly={needCode}
                onkeydown={(e) => e.key === 'Enter' && submit()}
                autofocus
              />
            </div>
            {#if needCode}
              <label class="login-pw-label" for="admin-otp" transition:fade={{ duration: 180 }}>Authenticator code:</label>
              <div class="pw-well" transition:fade={{ duration: 180 }}>
                <input
                  id="admin-otp"
                  class="login-pw-input"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="12"
                  bind:value={code}
                  placeholder="6-digit code"
                  title="Or enter one of your recovery codes"
                  onkeydown={(e) => e.key === 'Enter' && submit()}
                  use:focusOnMount
                />
              </div>
            {/if}
            {#if error}
              <p class="login-error" transition:fade={{ duration: 180 }} role="alert">{error}</p>
            {/if}
            <button class="login-go" onclick={submit} disabled={loading}>
              {loading ? 'Verifying…' : needCode ? 'Verify' : 'Log In'}
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <p class="login-footer" transition:fade={{ duration: 600, delay: 500 }}>
    SMac OS X 10.6.8 — Graphite
  </p>
</div>

<style>
  .login-screen {
    height: 100vh;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 30px;
    position: relative;
    overflow: hidden;
    font-family: var(--font, 'Lucida Grande', 'Helvetica Neue', Helvetica, Arial, sans-serif);
    color: var(--text, #edf1f5);
    font-size: calc(16px * var(--text-scale, 1));
  }

  /* ===== Ambience ===== */
  .login-wallpaper {
    position: absolute;
    inset: -3%;
    background: #10151d url('/wallpaper/aurora.jpg') center / cover no-repeat;
    animation: login-bg-drift 40s ease-in-out infinite alternate;
  }
  @keyframes login-bg-drift {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(1.07) translateY(-1%);
    }
  }
  .login-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.58);
  }
  /* 45° stripes + faint scanlines + vignette, stacked in one layer */
  .login-fx {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% 45%, transparent 34%, rgba(0, 0, 0, 0.72) 100%),
      repeating-linear-gradient(to bottom, rgba(0, 0, 0, 0) 0 2px, rgba(0, 0, 0, 0.07) 2px 3px),
      repeating-linear-gradient(
        45deg,
        rgba(0, 0, 0, 0.07) 0 6px,
        rgba(255, 255, 255, 0.012) 6px 12px
      );
  }

  /* ===== The panel: a solid 3D slab ===== */
  .login-center {
    position: relative;
    z-index: 1;
    perspective: 900px;
  }
  .login-panel {
    position: relative;
    transform-style: preserve-3d;
    transform-origin: center bottom;
    animation: panel-settle 800ms cubic-bezier(0.22, 1.1, 0.36, 1) both;
  }
  @keyframes panel-settle {
    0% {
      transform: rotateX(14deg) translateY(10px);
    }
    100% {
      transform: rotateX(3deg) translateY(0);
    }
  }
  .panel-face {
    position: relative;
    min-width: 340px;
    padding: 32px 44px 30px;
    text-align: center;
    image-rendering: pixelated;
    background:
      repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.035) 0 6px,
        rgba(0, 0, 0, 0.05) 6px 12px
      ),
      linear-gradient(to bottom, #66716f 0%, #4d5756 42%, #3a4342 100%);
    border: 3px solid;
    border-color: var(--bevel-light, #8b9694) var(--bevel-dark, #1a201f) var(--bevel-dark, #1a201f)
      var(--bevel-light, #8b9694);
    outline: 2px solid var(--bevel-outline, #000);
    /* inset bevel highlights + a hard-shadow ladder = real slab thickness */
    box-shadow:
      inset 2px 2px 0 rgba(255, 255, 255, 0.32),
      inset -3px -3px 0 rgba(0, 0, 0, 0.45),
      3px 4px 0 #2a3130,
      6px 8px 0 #232928,
      9px 12px 0 #1b201f,
      12px 16px 0 #141817,
      16px 22px 30px rgba(0, 0, 0, 0.7);
  }
  /* ===== Logo with z-depth ===== */
  .logo-stage {
    perspective: 260px;
    display: grid;
    place-items: center;
    margin-bottom: 8px;
  }
  .login-logo {
    width: 46px;
    height: 58px;
    color: #cdd6d4;
    filter: drop-shadow(1px 0 0 rgba(0, 0, 0, 0.75)) drop-shadow(-1px 0 0 rgba(0, 0, 0, 0.55))
      drop-shadow(0 1px 0 rgba(0, 0, 0, 0.75)) drop-shadow(0 -1px 0 rgba(0, 0, 0, 0.55))
      drop-shadow(2px 3px 0 rgba(0, 0, 0, 0.45)) drop-shadow(4px 6px 6px rgba(0, 0, 0, 0.5));
    animation: logo-hover 5s ease-in-out infinite;
  }
  @keyframes logo-hover {
    0%,
    100% {
      transform: translateY(0) rotateX(10deg) rotateY(-8deg) translateZ(6px);
    }
    50% {
      transform: translateY(-4px) rotateX(4deg) rotateY(8deg) translateZ(14px);
    }
  }

  /* ===== Extruded title ===== */
  .title-stage {
    perspective: 500px;
  }
  .extruded {
    display: inline-block;
    margin: 0 0 4px;
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 1.5px;
    color: #e9edf1;
    -webkit-text-stroke: 1px #000;
    /* outline under the fill, or thin fallback fonts (Windows) turn dark */
    paint-order: stroke fill;
    transform: rotateX(16deg) rotateZ(-1.5deg);
    transform-origin: center bottom;
    text-shadow:
      1px 1px 0 #000,
      2px 2px 0 #2a3130,
      3px 3px 0 #232928,
      4px 4px 0 #1b201f,
      5px 5px 0 #141817,
      6px 6px 6px rgba(0, 0, 0, 0.6);
    animation: title-tilt 6s ease-in-out infinite;
  }
  @keyframes title-tilt {
    0%,
    100% {
      transform: rotateX(16deg) rotateZ(-1.5deg);
    }
    50% {
      transform: rotateX(10deg) rotateZ(1.5deg);
    }
  }
  .login-header {
    margin-bottom: 26px;
  }
  .login-version {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: #ffd85a;
    text-shadow:
      1px 1px 0 #000,
      2px 2px 0 rgba(0, 0, 0, 0.5);
  }

  /* ===== Users ===== */
  .login-users {
    display: flex;
    gap: 16px;
    justify-content: center;
  }
  .user-slot {
    perspective: 700px;
  }

  /* ===== Password form ===== */
  .login-pw-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  .login-back {
    align-self: flex-start;
    background: none;
    border: none;
    color: var(--text-muted, #9ca6b2);
    cursor: pointer;
    font-size: 12px;
    font-family: inherit;
    padding: 0;
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.6);
    transition: color 0.12s;
  }
  .login-back:hover {
    color: #ffd85a;
  }
  .login-pw-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: #dbe2e8;
    text-transform: uppercase;
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.7);
  }
  /* sunken well */
  .pw-well {
    padding: 3px;
    background: linear-gradient(to bottom, #262d2c, #3b4443);
    border: 2px solid;
    border-color: #171c1b #7e8987 #7e8987 #171c1b;
    outline: 1px solid var(--bevel-outline, #000);
    box-shadow:
      inset 2px 2px 5px rgba(0, 0, 0, 0.8),
      inset -1px -1px 0 rgba(255, 255, 255, 0.14);
    transition: outline-color 0.12s;
  }
  .pw-well:focus-within {
    outline-color: #ffd85a;
    box-shadow:
      inset 2px 2px 5px rgba(0, 0, 0, 0.8),
      inset -1px -1px 0 rgba(255, 255, 255, 0.14),
      0 0 0 3px rgba(255, 216, 90, 0.35);
  }
  .login-pw-input {
    width: 196px;
    padding: 6px 8px;
    text-align: center;
    font: 13px var(--font, 'Lucida Grande', Tahoma, sans-serif);
    letter-spacing: 2px;
    color: var(--text, #edf1f5);
    background: #1e2423;
    border: 1px solid #0b0e0e;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.7);
    outline: none;
  }
  .login-pw-input::placeholder {
    color: #6d7876;
    letter-spacing: 1px;
  }

  /* error strip — beveled inset */
  .login-error {
    margin: 0;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #ffb3b3;
    background: #2a1c1c;
    border: 2px solid;
    border-color: #14100f #6e5a5a #6e5a5a #14100f;
    outline: 1px solid #000;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.7);
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.8);
  }

  /* gold beveled login button */
  .login-go {
    margin-top: 2px;
    padding: 7px 26px;
    cursor: pointer;
    font: 700 13px var(--font, 'Lucida Grande', Tahoma, sans-serif);
    letter-spacing: 1px;
    color: #241f06;
    background: linear-gradient(to bottom, #ffe27f 0%, #ffd85a 45%, #d9c34a 100%);
    border: 2px solid;
    border-color: #fff0b4 #7d6c1c #7d6c1c #fff0b4;
    outline: 1px solid var(--bevel-outline, #000);
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.6),
      inset -2px -2px 0 rgba(0, 0, 0, 0.28),
      2px 3px 0 rgba(0, 0, 0, 0.55),
      4px 6px 0 rgba(0, 0, 0, 0.28);
    transition:
      transform 0.12s ease,
      box-shadow 0.12s ease,
      filter 0.12s ease;
  }
  .login-go:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateY(-2px);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.6),
      inset -2px -2px 0 rgba(0, 0, 0, 0.28),
      3px 5px 0 rgba(0, 0, 0, 0.55),
      6px 9px 0 rgba(0, 0, 0, 0.3);
  }
  .login-go:active:not(:disabled) {
    transform: translateY(2px);
    border-color: #7d6c1c #fff0b4 #fff0b4 #7d6c1c;
    box-shadow: inset 2px 3px 4px rgba(0, 0, 0, 0.45);
  }
  .login-go:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .login-footer {
    position: relative;
    z-index: 1;
    color: rgba(255, 255, 255, 0.22);
    font-size: 11px;
    margin: 0;
    letter-spacing: 1.5px;
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.6);
  }

  @media (prefers-reduced-motion: reduce) {
    .login-wallpaper,
    .login-panel,
    .login-logo,
    .extruded,
    .login-go {
      animation: none !important;
      transition: none !important;
    }
    .login-panel {
      transform: none;
    }
    .login-logo {
      transform: none;
    }
    .extruded {
      transform: rotateX(14deg) rotateZ(-1.5deg);
    }
    .login-go:hover:not(:disabled) {
      transform: none;
    }
  }
</style>
